import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, AlertTriangle, CheckCircle2, Clock, Users, Info, Download, RefreshCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const YEARS = [2025, 2026, 2027];

// Client type donut palette (Ltd, Self-Assessment, Other)
const CLIENT_TYPE_COLORS = {
  ltd: "var(--navy)",
  self_assessment: "var(--gold)",
  other: "var(--ink-2)",
};

// Task status donut palette (In Progress / Completed / Overdue)
const TASK_STATUS_COLORS = {
  in_progress: "var(--navy)",
  completed: "var(--ok)",
  overdue: "var(--err)",
};

// Task type mapping — the spec lists 5 headline types. We map each to the
// backend service_type values so the counts stay accurate.
const TASK_TYPE_MAP = [
  { key: "Accounts", label: "Annual Accounts", tone: "var(--navy)" },
  { key: "Self-Assessment", label: "Self-Assessments", tone: "var(--gold)" },
  { key: "VAT", label: "VAT", tone: "var(--ok)" },
  { key: "Confirmation Statement", label: "Confirmation Statements", tone: "var(--warn)" },
  { key: "Company Formation", label: "Company Registrations", tone: "var(--err)" },
];

export default function MIDashboard() {
  const { user: me } = useAuth();
  const aiAllowed = me?.role === "admin" || me?.ai_enabled !== false;
  const [year, setYear] = useState(2026);
  const [data, setData] = useState(null);
  const [aiSummary, setAiSummary] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await api.get("/mi/dashboard", { params: { year } });
      if (!cancelled) setData(r.data);
    })();
    return () => { cancelled = true; };
  }, [year]);

  async function runAiSummary() {
    setBusy(true);
    try {
      const r = await api.get("/ai/daily-summary");
      setAiSummary(r.data.summary);
    } finally { setBusy(false); }
  }

  async function exportPdf() {
    const r = await api.get(`/mi/export-pdf?year=${year}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = `finchley_mi_${year}.pdf`; a.click();
    URL.revokeObjectURL(url);
  }

  if (!data) return <div className="p-12 text-[var(--ink-2)]">Loading MI Dashboard…</div>;

  return (
    <div className="p-8 md:p-10">
      {/* Header */}
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Management Information</div>
          <h1 className="font-display text-4xl">MI Dashboard.</h1>
          <div className="text-sm text-[var(--ink-2)] mt-1">Firm-wide analytics scoped by year — clients &amp; tasks.</div>
        </div>
        <div className="flex gap-2 items-center">
          <YearFilter year={year} onChange={setYear} />
          {aiAllowed && (
            <button data-testid="ai-summary-btn" onClick={runAiSummary} disabled={busy} className="btn-ghost flex items-center gap-2">
              <Sparkles size={14} strokeWidth={1.5} /> {busy ? "Thinking…" : "AI briefing"}
            </button>
          )}
          <button data-testid="export-pdf-btn" onClick={exportPdf} className="btn-primary flex items-center gap-2">
            <Download size={14} strokeWidth={1.5} /> Export PDF
          </button>
        </div>
      </div>

      {aiSummary && (
        <div className="card-flat p-5 mb-5 border-l-4 border-l-[var(--gold)]" data-testid="ai-summary">
          <div className="overline mb-2 flex items-center gap-2"><Sparkles size={12} strokeWidth={1.5} /> AI Briefing</div>
          <div className="text-sm whitespace-pre-wrap leading-relaxed">{aiSummary}</div>
        </div>
      )}

      {/* Companies House sync status (admin only) */}
      {me?.role === "admin" && <ChSyncStatus />}

      {/* Section 1 — Clients */}
      <ClientsSection data={data.clients_summary} />

      {/* Section 2 — Tasks */}
      <TasksSection data={data.tasks_summary} year={year} />
    </div>
  );
}

/* ---------- Companies House nightly sync widget ---------- */
function ChSyncStatus() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await api.get("/companies-house/sync-status");
      setStatus(r.data);
    } catch { /* silent — widget is non-critical */ }
  }
  useEffect(() => { load(); }, []);

  async function runNow() {
    if (!window.confirm("Run Companies House sync for every Ltd/LLP client now?\n\nThis re-fetches CH data and refreshes compliance deadlines. Safe to run any time.")) return;
    setBusy(true);
    try {
      const r = await api.post("/companies-house/run-nightly-sync-now");
      alert(`Sync complete.\n\n✓ ${r.data.ok} synced\n✗ ${r.data.fail} failed\n• ${r.data.skipped_no_company_no} skipped (no company number)\n📅 ${r.data.total_deadlines_upserted} deadlines refreshed\n⏱ ${r.data.duration_seconds.toFixed(1)}s`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  if (!status) return null;
  const last = status.recent_runs?.[0];
  return (
    <div className="card-flat p-4 mb-5 flex items-center justify-between border-l-4 border-l-[var(--navy)] flex-wrap gap-3" data-testid="ch-sync-status">
      <div className="flex-1 min-w-0">
        <div className="overline mb-1 flex items-center gap-1.5"><RefreshCcw size={11} strokeWidth={1.5} /> Companies House · nightly sync</div>
        <div className="text-xs text-[var(--ink-2)] font-mono-data">
          Scheduled: <span className="text-[var(--ink)]">{status.schedule}</span>
          {last && (
            <>
              {" · "}Last run: <span className="text-[var(--ink)]">{new Date(last.finished_at).toLocaleString("en-GB")}</span>
              {" · "}<span className="text-[var(--ok)]">✓ {last.ok}</span>
              {last.fail > 0 && <> · <span className="text-[var(--err)]">✗ {last.fail}</span></>}
              {last.skipped_no_company_no > 0 && <> · {last.skipped_no_company_no} skipped</>}
              {" · "}{last.total_deadlines_upserted} deadlines
            </>
          )}
          {!last && <> · <span className="italic">no runs yet — will trigger at the scheduled time</span></>}
        </div>
      </div>
      <button
        data-testid="ch-run-now-btn"
        onClick={runNow}
        disabled={busy}
        className="btn-ghost text-xs flex items-center gap-1.5 !py-1.5 !px-3"
      >
        <RefreshCcw size={11} strokeWidth={1.5} /> {busy ? "Syncing…" : "Run sync now"}
      </button>
    </div>
  );
}

/* ---------- Year filter ---------- */
function YearFilter({ year, onChange }) {
  return (
    <div className="inline-flex items-center border border-[var(--line)] rounded overflow-hidden bg-[var(--surface)]" data-testid="mi-year-filter">
      {YEARS.map((y) => {
        const on = y === year;
        return (
          <button
            key={y}
            data-testid={`mi-year-${y}`}
            onClick={() => onChange(y)}
            className={`px-3 py-1.5 text-sm font-mono-data transition ${on ? "bg-[var(--navy)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--bg)]"}`}
          >
            {y}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Section 1 · Clients ---------- */
function ClientsSection({ data }) {
  const total = data?.total_active ?? 0;
  const ltd = data?.ltd ?? 0;
  const sa = data?.self_assessment ?? 0;
  const other = data?.other ?? 0;

  const segments = [
    { key: "ltd", label: "Ltd", value: ltd, color: CLIENT_TYPE_COLORS.ltd },
    { key: "self_assessment", label: "Self-Assessment", value: sa, color: CLIENT_TYPE_COLORS.self_assessment },
    { key: "other", label: "Other", value: other, color: CLIENT_TYPE_COLORS.other },
  ];

  return (
    <section className="mb-6" data-testid="mi-section-clients">
      <SectionHeader label="Section 1" title="Clients" note={data?.year_note} />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="mi-clients-kpis">
        <KpiCard icon={Users} label="Total Active" value={total} testid="mi-kpi-clients-total" />
        <KpiCard label="Ltd" value={ltd} sub={pct(ltd, total)} tone="navy" testid="mi-kpi-clients-ltd" />
        <KpiCard label="Self-Assessment" value={sa} sub={pct(sa, total)} tone="gold" testid="mi-kpi-clients-sa" />
        <KpiCard label="Other" value={other} sub={pct(other, total)} tone="muted" testid="mi-kpi-clients-other" />
      </div>

      {/* Donut + breakdown list */}
      <div className="card-flat p-5" data-testid="mi-clients-chart">
        <div className="grid grid-cols-1 md:grid-cols-[220px,1fr] gap-8 items-center">
          <div className="flex justify-center">
            <Donut segments={segments} size={200} testid="mi-clients-donut" />
          </div>
          <BreakdownList segments={segments} total={total} testid="mi-clients-breakdown" />
        </div>
      </div>
    </section>
  );
}

/* ---------- Section 2 · Tasks ---------- */
function TasksSection({ data, year }) {
  const total = data?.total ?? 0;
  const completed = data?.completed ?? 0;
  const overdue = data?.overdue ?? 0;
  const inProgress = data?.in_progress ?? 0;
  const byService = data?.by_service_type || {};

  const statusSegments = [
    { key: "in_progress", label: "In Progress", value: inProgress, color: TASK_STATUS_COLORS.in_progress },
    { key: "completed", label: "Completed", value: completed, color: TASK_STATUS_COLORS.completed },
    { key: "overdue", label: "Overdue", value: overdue, color: TASK_STATUS_COLORS.overdue },
  ];

  const typeSegments = TASK_TYPE_MAP.map((t) => ({
    key: t.key,
    label: t.label,
    value: byService[t.key] || 0,
    color: t.tone,
  }));
  const typeTotal = typeSegments.reduce((a, s) => a + s.value, 0);

  return (
    <section data-testid="mi-section-tasks">
      <SectionHeader label="Section 2" title="Tasks" note={`Tasks whose deadline falls in ${year}.`} />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4" data-testid="mi-tasks-kpis">
        <KpiCard icon={Clock} label="Total Tasks" value={total} testid="mi-kpi-tasks-total" />
        <KpiCard icon={CheckCircle2} label="Completed" value={completed} sub={pct(completed, total)} tone="ok" testid="mi-kpi-tasks-completed" />
        <KpiCard icon={AlertTriangle} label="Overdue" value={overdue} sub={pct(overdue, total)} tone="err" testid="mi-kpi-tasks-overdue" />
      </div>

      {/* Two charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card-flat p-5" data-testid="mi-tasks-status-chart">
          <div className="overline mb-4">Status breakdown</div>
          <div className="grid grid-cols-1 md:grid-cols-[180px,1fr] gap-6 items-center">
            <div className="flex justify-center">
              <Donut segments={statusSegments} size={170} testid="mi-tasks-status-donut" />
            </div>
            <BreakdownList segments={statusSegments} total={inProgress + completed + overdue} testid="mi-tasks-status-breakdown" />
          </div>
        </div>

        <div className="card-flat p-5" data-testid="mi-tasks-type-chart">
          <div className="overline mb-4">Task type breakdown</div>
          <div className="grid grid-cols-1 md:grid-cols-[180px,1fr] gap-6 items-center">
            <div className="flex justify-center">
              <Donut segments={typeSegments} size={170} testid="mi-tasks-type-donut" />
            </div>
            <BreakdownList segments={typeSegments} total={typeTotal} testid="mi-tasks-type-breakdown" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Reusable UI atoms ---------- */
function SectionHeader({ label, title, note }) {
  return (
    <div className="flex items-end justify-between mb-3 border-b border-[var(--line)] pb-2">
      <div>
        <div className="overline">{label}</div>
        <h2 className="font-display text-2xl leading-tight">{title}</h2>
      </div>
      {note && (
        <div className="text-[11px] text-[var(--ink-2)] flex items-center gap-1 max-w-md text-right">
          <Info size={11} strokeWidth={1.5} className="shrink-0" /> {note}
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone, testid }) {
  const toneClass = tone === "err" ? "text-[var(--err)]"
    : tone === "ok" ? "text-[var(--ok)]"
    : tone === "gold" ? "text-[var(--warn)]"
    : tone === "navy" ? "text-[var(--navy)]"
    : tone === "muted" ? "text-[var(--ink-2)]"
    : "text-[var(--ink)]";
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 overline mb-2">
        {Icon && <Icon size={11} strokeWidth={1.5} />} {label}
      </div>
      <div className={`font-display text-3xl leading-none ${toneClass}`}>{value}</div>
      {sub && <div className="text-[11px] text-[var(--ink-2)] mt-1 font-mono-data">{sub}</div>}
    </div>
  );
}

function pct(n, total) {
  if (!total) return "0%";
  return `${Math.round((n / total) * 100)}%`;
}

function BreakdownList({ segments, total, testid }) {
  return (
    <ul className="space-y-1.5" data-testid={testid}>
      {segments.map((s) => {
        const p = total ? Math.round((s.value / total) * 100) : 0;
        return (
          <li key={s.key} className="flex items-center gap-3 text-sm" data-testid={`${testid}-${s.key}`}>
            <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
            <span className="flex-1 truncate">{s.label}</span>
            <span className="font-mono-data text-xs w-10 text-right">{s.value}</span>
            <span className="font-mono-data text-xs w-10 text-right text-[var(--ink-2)]">{p}%</span>
          </li>
        );
      })}
      {total === 0 && (
        <li className="text-xs text-[var(--ink-2)] pt-2 border-t border-[var(--line)] mt-2">
          No data for the selected year.
        </li>
      )}
    </ul>
  );
}

/**
 * Donut — SVG donut chart from segments. Uses stroke-dasharray on concentric
 * circles so no chart library is needed.
 */
function Donut({ segments, size = 180, thickness = 26, testid }) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  const arcs = segments.map((s, i) => {
    if (!total || s.value <= 0) return null;
    const frac = s.value / total;
    const dash = frac * circumference;
    const arc = (
      <circle
        key={s.key}
        r={radius}
        cx={size / 2}
        cy={size / 2}
        fill="transparent"
        stroke={s.color}
        strokeWidth={thickness}
        strokeDasharray={`${dash} ${circumference - dash}`}
        strokeDashoffset={-offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    );
    offset += dash;
    return arc;
  });

  return (
    <svg width={size} height={size} data-testid={testid} role="img" aria-label="Donut chart">
      <circle
        r={radius}
        cx={size / 2}
        cy={size / 2}
        fill="transparent"
        stroke="var(--line)"
        strokeWidth={thickness}
      />
      {arcs}
      <text x="50%" y="46%" textAnchor="middle" className="font-display" style={{ fontSize: size * 0.18, fill: "var(--ink)" }}>{total}</text>
      <text x="50%" y="60%" textAnchor="middle" style={{ fontSize: 10, letterSpacing: "0.14em", fill: "var(--ink-2)", fontFamily: "'JetBrains Mono', monospace" }}>TOTAL</text>
    </svg>
  );
}
